import { useCurrentClient } from '@mysten/dapp-kit-react'
import { useQuery } from '@tanstack/react-query'
import { Amount, PositionConfigInfo, TypeArgument } from '@kunalabs-io/kai'
import Decimal from 'decimal.js'

import { useWalletBalances } from '@/shared/hooks/useWalletBalances'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'

import { useRouter } from 'contexts/SwapRouterContext'

export const repayDebtInfoQueryKey = (positionId: string) =>
  ['repay-debt-info', positionId] as const

export const useRepayDebtInfo = (
  configInfo: PositionConfigInfo<string, string, TypeArgument>,
  positionInfo: PositionInfo,
  amountPercentage: number
) => {
  const walletBalances = useWalletBalances()

  let balanceX: Amount | undefined = undefined
  let balanceY: Amount | undefined = undefined
  if (walletBalances.infos && configInfo) {
    balanceX = walletBalances.infos.get(configInfo.X.typeName)
    balanceY = walletBalances.infos.get(configInfo.Y.typeName)
  }

  const client = useCurrentClient()
  const { router } = useRouter()

  const repayDebtInfoRes = useQuery({
    queryKey: repayDebtInfoQueryKey(positionInfo.positionId),
    enabled: Boolean(positionInfo.lastPosition),
    queryFn: async () => {
      if (!positionInfo.lastPosition) {
        throw new Error('Invariant violation: positionInfo.lastPosition is undefined')
      }
      const radi = await positionInfo.lastPosition.getReduceAmounts(client, router, {
        factor: new Decimal(amountPercentage).div(100),
        positionCapId: positionInfo.positionCapId,
      })

      const xDecimals = positionInfo.lastPosition.X.decimals
      const yDecimals = positionInfo.lastPosition.Y.decimals

      return {
        repayDebtX: Amount.fromInt(
          BigInt((radi.repayDebtX * 10 ** xDecimals * 1.01).toFixed(0)),
          xDecimals
        ).toNumber(),
        repayDebtY: Amount.fromInt(
          BigInt((radi.repayDebtY * 10 ** yDecimals * 1.01).toFixed(0)),
          yDecimals
        ).toNumber(),
      }
    },
  })

  let additionalAmountTokenNum = 0
  if (repayDebtInfoRes.data?.repayDebtX) {
    additionalAmountTokenNum += 1
  }
  if (repayDebtInfoRes.data?.repayDebtY) {
    additionalAmountTokenNum += 1
  }

  let errorNotEnoughTokens = false
  if (
    repayDebtInfoRes.data?.repayDebtX &&
    balanceX &&
    balanceX.toNumber() < repayDebtInfoRes.data.repayDebtX
  ) {
    errorNotEnoughTokens = true
  }
  if (
    repayDebtInfoRes.data?.repayDebtY &&
    balanceY &&
    balanceY.toNumber() < repayDebtInfoRes.data.repayDebtY
  ) {
    errorNotEnoughTokens = true
  }

  return {
    isLoading: repayDebtInfoRes.isLoading,
    additionalAmountTokenNum,
    errorNotEnoughTokens,
    repayDebtX: repayDebtInfoRes?.data?.repayDebtX,
    repayDebtY: repayDebtInfoRes?.data?.repayDebtY,
  }
}
